import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Search, MapPin, Trash2, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, KeyRound, ArrowUpRight, Lock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { BulkBar, SelectAllCheckbox, RowCheckbox } from "@/components/BulkBar";
import { PhotoUploader } from "@/components/PhotoUploader";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import GallonsField from "@/components/GallonsField";
import WaterCalculatorPopover from "@/components/WaterCalculatorPopover";
import AddressFields from "@/components/AddressFields";
import CardknoxPayDialog from "@/components/CardknoxPayDialog";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/lib/log";

const blank = {
  name: "", business_name: "", llc_name: "", email: "", contact_phone: "", billing_phone: "", address: "",
  city: "", state: "", zip_code: "",
  secondary_invoice_email: "",
  schedule: "on_call", schedule_day: null, default_gallons: 0, tax_rate: 0,
  tax_exempt: false, tax_exempt_number: "", tax_exempt_form_data_url: "", tax_exempt_form_data_url_2: "",
  notes: "", portal_email: "", refill_empty_jugs: false,
  delivery_locked: false,
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const dayLabel = (d) => (d === null || d === undefined || d < 0 || d > 6 ? null : DAY_LABELS[d]);

function CustomerForm({ form, setForm }) {
  const { user } = useAuth();
  const canEditPrices = user?.role === "admin";
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Contact name *</Label><Input className="rounded-sm" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="customer-name-input" /></div>
        <div><Label>Business name</Label><Input className="rounded-sm" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="customer-business-name" /></div>
      </div>
      <div><Label>LLC or trade name</Label><Input className="rounded-sm" value={form.llc_name || ""} onChange={(e) => set("llc_name", e.target.value)} data-testid="customer-llc-name" /></div>
      <div>
        <Label>Billing address</Label>
        <AddressFields
          value={{ address: form.address, city: form.city, state: form.state, zip_code: form.zip_code }}
          onChange={(patch) => setForm({ ...form, ...patch })}
          showLabels={false}
          testIdPrefix="customer-address"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label>Contact phone</Label><Input className="rounded-sm" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="customer-contact-phone" /></div>
        <div><Label>Billing phone</Label><Input className="rounded-sm" value={form.billing_phone} onChange={(e) => set("billing_phone", e.target.value)} data-testid="customer-billing-phone" /></div>
        <div><Label>Contact email</Label><Input className="rounded-sm" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
      </div>
      <div><Label>Portal login email</Label><Input className="rounded-sm" type="email" value={form.portal_email} onChange={(e) => set("portal_email", e.target.value)} data-testid="customer-portal-email" placeholder="grants portal access" /></div>
      <div>
        <Label>Secondary invoice email <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
        <Input
          className="rounded-sm"
          type="email"
          value={form.secondary_invoice_email || ""}
          onChange={(e) => set("secondary_invoice_email", e.target.value)}
          placeholder="copy@example.com — CC'd on every invoice & quote"
          data-testid="customer-secondary-invoice-email"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label>Delivery schedule</Label>
          <Select value={form.schedule} onValueChange={(v) => set("schedule", v)}>
            <SelectTrigger className="rounded-sm" data-testid="customer-schedule-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="biweekly">Bi-weekly</SelectItem>
              <SelectItem value="on_call">On call</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Day of week {form.schedule === "on_call" && <span className="text-muted-foreground text-xs font-normal">(preferred)</span>}</Label>
          <Select
            value={form.schedule_day === null || form.schedule_day === undefined ? "none" : String(form.schedule_day)}
            onValueChange={(v) => set("schedule_day", v === "none" ? null : parseInt(v, 10))}
          >
            <SelectTrigger className="rounded-sm" data-testid="customer-schedule-day-select"><SelectValue placeholder="No fixed day" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No fixed day</SelectItem>
              <SelectItem value="0">Monday</SelectItem>
              <SelectItem value="1">Tuesday</SelectItem>
              <SelectItem value="2">Wednesday</SelectItem>
              <SelectItem value="3">Thursday</SelectItem>
              <SelectItem value="4">Friday</SelectItem>
              <SelectItem value="5">Saturday</SelectItem>
              <SelectItem value="6">Sunday</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="flex items-center gap-1">Tax rate % {!canEditPrices && <Lock className="w-3 h-3 text-muted-foreground" />}</Label>
          <Input className="rounded-sm" type="number" step="0.001" value={form.tax_rate}
                 onChange={(e) => set("tax_rate", parseFloat(e.target.value || 0))}
                 readOnly={!canEditPrices} disabled={!canEditPrices} />
        </div>
      </div>
      <div className="flex items-center gap-3 border border-border rounded-sm p-3">
        <Switch checked={form.refill_empty_jugs} onCheckedChange={(v) => set("refill_empty_jugs", v)} data-testid="customer-refill-jugs-switch" />
        <div>
          <Label className="cursor-pointer">Refill empty 5-gallon jugs on-site</Label>
          <p className="text-xs text-muted-foreground">Driver will swap and refill empty carboys when they visit.</p>
        </div>
      </div>
      <div className={`flex items-center gap-3 border rounded-sm p-3 ${form.delivery_locked ? "border-destructive bg-destructive/5" : "border-border"}`}>
        <Switch checked={!!form.delivery_locked} onCheckedChange={(v) => set("delivery_locked", !!v)} data-testid="customer-delivery-lock-switch" />
        <div>
          <Label className="cursor-pointer flex items-center gap-1">Delivery lock (non-payment) {form.delivery_locked && <Lock className="w-3 h-3 text-destructive" />}</Label>
          <p className="text-xs text-muted-foreground">When ON, this customer cannot request deliveries from the portal. Locks automatically at 2+ unpaid invoices and unlocks when they&apos;re paid down. New self-signups stay locked until a payment card is on file.</p>
        </div>
      </div>
      <div className="border border-border rounded-sm p-3 space-y-3" data-testid="customer-tax-exempt-block">
        <div className="flex items-start gap-3">
          <Switch
            checked={!!form.tax_exempt}
            onCheckedChange={(v) => set("tax_exempt", !!v)}
            disabled={!canEditPrices}
            data-testid="customer-tax-exempt-switch"
          />
          <div className="flex-1">
            <Label className="cursor-pointer flex items-center gap-1">
              Tax exempt organization {!canEditPrices && <Lock className="w-3 h-3 text-muted-foreground" />}
            </Label>
            <p className="text-xs text-muted-foreground">When ON, every invoice for this customer is generated with $0 tax. Keep a copy of the exemption certificate on file below.</p>
          </div>
        </div>
        {form.tax_exempt && (
          <div className="pl-12 space-y-3">
            <div>
              <Label className="text-xs">Exempt certificate number</Label>
              <Input
                className="rounded-sm mono-num"
                placeholder="e.g. 12-3456789"
                value={form.tax_exempt_number || ""}
                onChange={(e) => set("tax_exempt_number", e.target.value)}
                readOnly={!canEditPrices} disabled={!canEditPrices}
                data-testid="customer-tax-exempt-number"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">ST-119.4 (photo or PDF)</Label>
                <PhotoUploader
                  value={form.tax_exempt_form_data_url || ""}
                  onChange={(v) => set("tax_exempt_form_data_url", v)}
                  label="ST-119.4"
                  testId="customer-tax-exempt-upload"
                  disabled={!canEditPrices}
                  acceptPdf
                />
              </div>
              <div>
                <Label className="text-xs">Tax exempt form</Label>
                <PhotoUploader
                  value={form.tax_exempt_form_data_url_2 || ""}
                  onChange={(v) => set("tax_exempt_form_data_url_2", v)}
                  label="Tax exempt form"
                  testId="customer-tax-exempt-upload-2"
                  disabled={!canEditPrices}
                  acceptPdf
                />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">Slot 1 is the NY ST-119.4 affirmation. Slot 2 is the customer&apos;s tax exempt form (e.g. ST-119.1 / sales-tax permit). Both files are required. PDFs and photos both work.</p>
          </div>
        )}
      </div>
      <div><Label>Notes</Label><Textarea className="rounded-sm" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
    </div>
  );
}

function PoolsManager({ customerId }) {
  const [pools, setPools] = useState([]);
  const [name, setName] = useState("");
  const [addr, setAddr] = useState({ address: "", city: "", state: "", zip_code: "" });
  const [tank, setTank] = useState(0);
  const [water, setWater] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ entry_photo_data_url: "", entry_instructions: "" });

  const load = useCallback(() => api.get(`/customers/${customerId}/pools`).then((r) => setPools(r.data)).catch(() => {}), [customerId]);
  useEffect(() => { if (customerId) load(); }, [customerId, load]);

  const add = async () => {
    if (!name || !addr.address) return toast.error("Pool name and street address required");
    try {
      await api.post(`/customers/${customerId}/pools`, {
        name,
        address: addr.address,
        city: addr.city || "",
        state: addr.state || "",
        zip_code: addr.zip_code || "",
        tank_size: Number(tank || 0),
        water_gallons: Number(water || 0),
      });
      setName(""); setAddr({ address: "", city: "", state: "", zip_code: "" }); setTank(0); setWater(0); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const updateTank = async (p, newSize) => {
    try {
      await api.put(`/pools/${p.id}`, {
        name: p.name, address: p.address,
        city: p.city || "", state: p.state || "", zip_code: p.zip_code || "",
        tank_size: Number(newSize || 0),
        water_gallons: Number(p.water_gallons || 0),
        notes: p.notes || "",
        entry_photo_data_url: p.entry_photo_data_url || "", entry_instructions: p.entry_instructions || "",
      });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const updateWater = async (p, newWater) => {
    try {
      await api.put(`/pools/${p.id}`, {
        name: p.name, address: p.address,
        city: p.city || "", state: p.state || "", zip_code: p.zip_code || "",
        tank_size: Number(p.tank_size || 0),
        water_gallons: Number(newWater || 0),
        notes: p.notes || "",
        entry_photo_data_url: p.entry_photo_data_url || "", entry_instructions: p.entry_instructions || "",
      });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const openEntryEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      entry_photo_data_url: p.entry_photo_data_url || "",
      entry_instructions: p.entry_instructions || "",
    });
  };
  const saveEntry = async (p) => {
    try {
      await api.put(`/pools/${p.id}`, {
        name: p.name, address: p.address,
        city: p.city || "", state: p.state || "", zip_code: p.zip_code || "",
        tank_size: Number(p.tank_size || 0),
        water_gallons: Number(p.water_gallons || 0),
        notes: p.notes || "",
        entry_photo_data_url: editForm.entry_photo_data_url || "",
        entry_instructions: editForm.entry_instructions || "",
      });
      toast.success("Entry info saved");
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this pool?")) return;
    try { await api.delete(`/pools/${pid}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-3 border-t border-border pt-4 mt-2">
      <p className="overline">Pool locations · {pools.length}</p>
      <div className="border border-border rounded-sm p-3 space-y-3 bg-secondary/20">
        <div className="grid grid-cols-12 gap-2">
          <Input className="col-span-12 sm:col-span-11 rounded-sm" placeholder="Pool name (e.g. Main Pool)" value={name}
                 onChange={(e) => setName(e.target.value)} data-testid="pool-name-input" />
          <Button onClick={add} variant="outline" className="col-span-12 sm:col-span-1 rounded-sm" data-testid="add-pool-button"><Plus className="w-4 h-4" /></Button>
        </div>
        <AddressFields
          value={addr}
          onChange={(patch) => setAddr({ ...addr, ...patch })}
          testIdPrefix="pool-address"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Chlorine tank size (gallons)</Label>
            <Input className="rounded-sm mono-num mt-1" placeholder="e.g. 50" type="number" step="0.1" value={tank}
                   onChange={(e) => setTank(e.target.value)} data-testid="pool-tank-input" />
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">Delivery quantity per visit</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Pool water capacity (gallons)</Label>
            <div className="mt-1">
              <GallonsField value={water} onChange={setWater} testId="pool-water-input" inputClassName="rounded-sm mono-num" compact />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">Use calculator for pool volume</p>
          </div>
        </div>
      </div>
      {pools.length === 0 && <p className="text-xs text-muted-foreground">No pools yet. Add the location and tank size for each pool.</p>}
      {pools.map((p) => (
        <div key={p.id} className="border border-border rounded-sm" data-testid={`pool-row-${p.id}`}>
          <div className="flex items-center gap-2 px-3 py-2 text-sm">
            <div className="flex-1">
              <div className="font-medium">{p.name}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.address}</div>
            </div>
            {p.entry_photo_data_url ? (
              <LivePhotoSurface value={p.entry_photo_data_url} alt="entry" className="w-10 h-10 rounded-sm border border-border" testId={`pool-entry-thumb-${p.id}`} />
            ) : null}
            <div className="flex items-center gap-1" title="Chlorine tank size">
              <Label className="text-xs">Tank</Label>
              <Input type="number" step="0.1" defaultValue={p.tank_size || 0}
                     onBlur={(e) => updateTank(p, e.target.value)}
                     className="w-20 h-8 rounded-sm mono-num text-right" data-testid={`pool-tank-${p.id}`} />
              <span className="text-xs text-muted-foreground">gal</span>
            </div>
            <div className="flex items-center gap-1" title="Pool water capacity">
              <Label className="text-xs">Water</Label>
              <Input type="number" step="1" key={`water-${p.id}-${p.water_gallons || 0}`} defaultValue={p.water_gallons || 0}
                     onBlur={(e) => updateWater(p, e.target.value)}
                     className="w-24 h-8 rounded-sm mono-num text-right" data-testid={`pool-water-${p.id}`} />
              <span className="text-xs text-muted-foreground">gal</span>
              <WaterCalculatorPopover
                onApply={(g) => updateWater(p, g)}
                testId={`pool-water-calc-${p.id}`}
              />
            </div>
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address)}`} target="_blank" rel="noopener noreferrer"
               className="text-primary hover:underline text-xs flex items-center gap-1" data-testid={`pool-maps-${p.id}`}>
              <ExternalLink className="w-3 h-3" /> Maps
            </a>
            <Button size="sm" variant="ghost" onClick={() => (editingId === p.id ? setEditingId(null) : openEntryEdit(p))} className="rounded-sm text-primary hover:text-primary" data-testid={`pool-entry-edit-${p.id}`}>
              <KeyRound className="w-3.5 h-3.5 mr-1" /> Entry
            </Button>
            <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="rounded-sm text-destructive hover:text-destructive" data-testid={`pool-delete-${p.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          {editingId === p.id && (
            <div className="border-t border-border bg-secondary/30 px-3 py-3 space-y-3" data-testid={`pool-entry-form-${p.id}`}>
              <PhotoUploader
                value={editForm.entry_photo_data_url}
                onChange={(v) => setEditForm({ ...editForm, entry_photo_data_url: v })}
                label="Entry / gate photo"
                testId={`pool-entry-photo-${p.id}`}
              />
              <div>
                <Label className="text-xs">Combination / gate code / instructions</Label>
                <Textarea
                  className="rounded-sm font-mono text-sm"
                  placeholder="e.g. Gate code 1234#&#10;Lockbox by garage, combo 4-9-1&#10;Dogs are friendly"
                  rows={3}
                  value={editForm.entry_instructions}
                  onChange={(e) => setEditForm({ ...editForm, entry_instructions: e.target.value })}
                  data-testid={`pool-entry-text-${p.id}`}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="rounded-sm">Cancel</Button>
                <Button size="sm" onClick={() => saveEntry(p)} className="rounded-sm" data-testid={`pool-entry-save-${p.id}`}>Save entry info</Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return dir === "asc" ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />;
}

function DraftPoolsList({ pools, onChange, draft, setDraft }) {
  const { name, address, city, state, zip_code, tank, water } = draft;
  const update = (k, v) => setDraft({ ...draft, [k]: v });
  const updateMany = (patch) => setDraft({ ...draft, ...patch });

  const add = () => {
    if (!name || !address) return toast.error("Pool name and street address required");
    onChange([...pools, {
      _uid: `dp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name, address,
      city: city || "", state: state || "", zip_code: zip_code || "",
      tank_size: Number(tank || 0),
      water_gallons: Number(water || 0),
    }]);
    setDraft({ name: "", address: "", city: "", state: "", zip_code: "", tank: 0, water: 0 });
  };
  const remove = (i) => onChange(pools.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3 border-t border-border pt-4 mt-2">
      <p className="overline">Pool locations · {pools.length}</p>
      <div className="border border-border rounded-sm p-3 space-y-3 bg-secondary/20">
        <div className="grid grid-cols-12 gap-2">
          <Input className="col-span-12 sm:col-span-11 rounded-sm" placeholder="Pool name (e.g. Main Pool)" value={name}
                 onChange={(e) => update("name", e.target.value)} data-testid="draft-pool-name" />
          <Button onClick={add} variant="outline" className="col-span-12 sm:col-span-1 rounded-sm" data-testid="draft-add-pool" title="Add this pool to the list">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <AddressFields
          value={{ address, city, state, zip_code }}
          onChange={(patch) => updateMany(patch)}
          testIdPrefix="draft-pool-address"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Chlorine tank size (gallons)</Label>
            <Input className="rounded-sm mono-num mt-1" placeholder="e.g. 50" type="number" step="0.1" value={tank}
                   onChange={(e) => update("tank", e.target.value)} data-testid="draft-pool-tank" />
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">Delivery quantity per visit</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Pool water capacity (gallons)</Label>
            <div className="mt-1">
              <GallonsField value={water} onChange={(v) => update("water", v)} testId="draft-pool-water" inputClassName="rounded-sm mono-num" compact />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">Use calculator for pool volume</p>
          </div>
        </div>
        <Button onClick={add} variant="outline" className="w-full rounded-sm h-9 text-xs" data-testid="draft-add-pool-big">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add this pool to list
        </Button>
        {(name || address) && pools.length === 0 && (
          <p className="text-[11px] text-warning font-medium pt-1">⚠ Click &ldquo;Add this pool to list&rdquo; before saving — the customer will save without this pool otherwise.</p>
        )}
      </div>
      {pools.length === 0 && !name && !address && <p className="text-xs text-muted-foreground">Optional — pools can also be added later. Adding them here saves time.</p>}
      {pools.map((p, i) => (
        <div key={p._uid || `dpool-${i}`} className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm" data-testid={`draft-pool-row-${i}`}>
          <div className="flex-1">
            <div className="font-medium">{p.name}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.address}</div>
          </div>
          <div className="text-xs text-muted-foreground mono-num text-right">
            <div>Tank {p.tank_size || 0} gal</div>
            {p.water_gallons > 0 && <div>Water {p.water_gallons} gal</div>}
          </div>
          <Button size="sm" variant="ghost" onClick={() => remove(i)} className="rounded-sm text-destructive hover:text-destructive" data-testid={`draft-pool-remove-${i}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export default function Customers() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [draftPools, setDraftPools] = useState([]);
  const [draftPoolDraft, setDraftPoolDraft] = useState({ name: "", address: "", latitude: null, longitude: null, tank: 0, water: 0 });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  // Quick "Add credit card on behalf of customer" flow from the customer list.
  // Holds the customer whose card the staff is currently entering; null = closed.
  const [cardCustomer, setCardCustomer] = useState(null);

  const load = useCallback(() => api.get("/customers").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const bulkDelete = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} customer${ids.length === 1 ? "" : "s"}? Their pools, tickets and invoices will also be removed.`)) return;
    try {
      await api.post("/customers/bulk-delete", { ids });
      toast.success(`Deleted ${ids.length}`);
      setSelected([]); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const filtered = useMemo(() => {
    let arr = list.slice();
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.business_name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.contact_phone || "").toLowerCase().includes(q) ||
        (c.billing_phone || "").toLowerCase().includes(q) ||
        (c.address || "").toLowerCase().includes(q)
      );
    }
    const cmp = (a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const get = (c) => {
        switch (sortKey) {
          case "name": return (c.business_name || c.name || "").toLowerCase();
          case "address": return (c.address || "").toLowerCase();
          case "schedule": return (c.schedule || "").toLowerCase();
          case "gallons": return Number(c.total_tank_gallons || 0);
          case "pools": return Number(c.pools_count || 0);
          default: return "";
        }
      };
      const va = get(a), vb = get(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    };
    arr.sort(cmp);
    return arr;
  }, [list, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const openNew = () => { setEditing(null); setForm(blank); setDraftPools([]); setDraftPoolDraft({ name: "", address: "", city: "", state: "", zip_code: "", tank: 0, water: 0 }); setOpen(true); };
  const openEdit = async (c) => {
    // List endpoint omits the heavy tax-exempt file data URLs to keep page loads
    // fast — re-fetch the full customer so the form can show them on edit.
    let full = c;
    try {
      const r = await api.get(`/customers/${c.id}`);
      full = r.data || c;
    } catch (e) { logError("Full customer fetch failed, falling back to list row:", e); }
    setEditing(full);
    setForm({
      ...blank, ...full,
      name: full.name || "", business_name: full.business_name || "",
      email: full.email || "", contact_phone: full.contact_phone || "",
      billing_phone: full.billing_phone || "", address: full.address || "",
      city: full.city || "", state: full.state || "", zip_code: full.zip_code || "",
      secondary_invoice_email: full.secondary_invoice_email || "",
      notes: full.notes || "", portal_email: full.portal_email || "",
      refill_empty_jugs: !!full.refill_empty_jugs,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Contact name is required");
    if (form.tax_exempt && !form.tax_exempt_form_data_url && !form.tax_exempt_form_data_url_2) {
      return toast.error("Upload at least the ST-119.4 file for tax-exempt customers");
    }
    if (form.tax_exempt && (!form.tax_exempt_form_data_url || !form.tax_exempt_form_data_url_2)) {
      // Non-blocking nudge — existing tax-exempt customers may only have the ST-119.4
      // on file from before the second slot existed. Don't lock them out of edits.
      toast.warning("Heads up — both ST-119.4 and the Tax exempt form should be on file. Saving anyway.");
    }
    const payload = { ...form };
    if (!payload.portal_email) delete payload.portal_email;
    if (!payload.email) delete payload.email;
    if (!payload.secondary_invoice_email) delete payload.secondary_invoice_email;

    // Auto-flush a typed-but-not-yet-added draft pool so users don't lose data
    // by forgetting to click the "Add this pool to list" button before save.
    let poolsToCreate = draftPools.slice();
    if (!editing && draftPoolDraft.name && draftPoolDraft.address) {
      poolsToCreate.push({
        name: draftPoolDraft.name,
        address: draftPoolDraft.address,
        city: draftPoolDraft.city || "",
        state: draftPoolDraft.state || "",
        zip_code: draftPoolDraft.zip_code || "",
        tank_size: Number(draftPoolDraft.tank || 0),
        water_gallons: Number(draftPoolDraft.water || 0),
      });
    }

    try {
      let createdId = editing?.id;
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
      } else {
        const r = await api.post("/customers", payload);
        createdId = r.data?.id;
      }
      // Flush draft pools created during the New-customer flow
      let poolsCreated = 0;
      let poolsFailed = 0;
      if (!editing && poolsToCreate.length > 0 && createdId) {
        for (const p of poolsToCreate) {
          try {
            await api.post(`/customers/${createdId}/pools`, {
              name: p.name,
              address: p.address,
              city: p.city || "",
              state: p.state || "",
              zip_code: p.zip_code || "",
              tank_size: Number(p.tank_size || 0),
              water_gallons: Number(p.water_gallons || 0),
            });
            poolsCreated += 1;
          } catch (err) {
            poolsFailed += 1;
            logError("Pool create failed:", err?.response?.data || err?.message);
          }
        }
      }
      if (poolsFailed > 0) {
        toast.error(`Customer created, but ${poolsFailed} pool${poolsFailed === 1 ? "" : "s"} failed to save. Open the customer to retry.`);
      } else if (editing) {
        toast.success("Customer updated");
      } else {
        toast.success(`Customer created${poolsCreated ? ` with ${poolsCreated} pool${poolsCreated === 1 ? "" : "s"}` : ""}`);
      }
      setOpen(false);
      setEditing(null);
      setForm(blank);
      setDraftPools([]);
      setDraftPoolDraft({ name: "", address: "", city: "", state: "", zip_code: "", tank: 0, water: 0 });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.business_name || c.name}"? This will also delete their pools, tickets, and invoices.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
        <div>
          <p className="overline">Directory</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">Customers</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); setDraftPools([]); } }}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-sm h-10" data-testid="add-customer-button">
              <Plus className="w-4 h-4 mr-2" /> New customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? "Edit customer" : "New customer"}</DialogTitle>
              <DialogDescription>Billing info, schedule, portal access, and pool locations.</DialogDescription>
            </DialogHeader>
            <CustomerForm form={form} setForm={setForm} />
            {editing ? (
              <PoolsManager customerId={editing.id} />
            ) : (
              <DraftPoolsList pools={draftPools} onChange={setDraftPools} draft={draftPoolDraft} setDraft={setDraftPoolDraft} />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Close</Button>
              <Button onClick={save} className="rounded-sm" data-testid="save-customer-button">{editing ? "Save changes" : "Create customer"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input className="rounded-sm pl-9" placeholder="Search by name, business, email, phone, address…"
               value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customer-search-input" />
      </div>

      <BulkBar selected={selected} allIds={filtered.map((c) => c.id)} onChangeSelected={setSelected}
               onBulkDelete={bulkDelete} testId="customer-bulk-bar" />

      <div className="border border-border overflow-x-auto">
        <div className="min-w-[900px]">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-border overline items-center">
          <div className="col-span-1"><SelectAllCheckbox selected={selected} allIds={filtered.map((c) => c.id)} onChange={setSelected} testId="customer-select-all" /></div>
          <button onClick={() => toggleSort("name")} className="col-span-3 flex items-center gap-1 hover:text-primary text-left" data-testid="sort-name">
            Customer <SortIcon active={sortKey === "name"} dir={sortDir} />
          </button>
          <button onClick={() => toggleSort("address")} className="col-span-3 flex items-center gap-1 hover:text-primary text-left" data-testid="sort-address">
            Location <SortIcon active={sortKey === "address"} dir={sortDir} />
          </button>
          <button onClick={() => toggleSort("schedule")} className="col-span-2 flex items-center gap-1 hover:text-primary text-left" data-testid="sort-schedule">
            Delivery <SortIcon active={sortKey === "schedule"} dir={sortDir} />
          </button>
          <button onClick={() => toggleSort("gallons")} className="col-span-1 flex items-center gap-1 hover:text-primary text-left" data-testid="sort-gallons">
            Total gal <SortIcon active={sortKey === "gallons"} dir={sortDir} />
          </button>
          <button onClick={() => toggleSort("pools")} className="col-span-1 flex items-center gap-1 hover:text-primary text-left" data-testid="sort-pools">
            Pools <SortIcon active={sortKey === "pools"} dir={sortDir} />
          </button>
          <div className="col-span-1 text-right">·</div>
        </div>
        {filtered.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted-foreground">{search ? "No matches." : "No customers yet."}</div>}
        {filtered.map((c) => (
          <div key={c.id} className="grid grid-cols-12 px-6 py-4 border-b border-border last:border-b-0 hover:bg-secondary/40 items-center" data-testid={`customer-row-${c.id}`}>
            <div className="col-span-1"><RowCheckbox id={c.id} selected={selected} onChange={setSelected} testId={`customer-check-${c.id}`} /></div>
            <Link to={`/admin/customers/${c.id}`} className="col-span-3 hover:text-primary" data-testid={`customer-name-link-${c.id}`}>
              <div className="font-medium flex items-center gap-1.5">
                {c.business_name || c.name}
                {c.delivery_locked && <Lock className="w-3.5 h-3.5 text-destructive shrink-0" data-testid={`customer-locked-badge-${c.id}`} />}
              </div>
              {c.business_name && <div className="text-xs text-muted-foreground">{c.name}</div>}
            </Link>
            <div className="col-span-3 text-sm text-muted-foreground truncate">{c.address || "—"}</div>
            <div className="col-span-2 text-sm capitalize">
              {(c.schedule || "").replace("_", " ")}
              {dayLabel(c.schedule_day) && (
                <span className="ml-1 text-xs text-muted-foreground normal-case" data-testid={`customer-schedule-day-${c.id}`}>· {dayLabel(c.schedule_day)}</span>
              )}
            </div>
            <div className="col-span-1 mono-num text-sm">{c.total_tank_gallons || 0}</div>
            <div className="col-span-1 mono-num text-sm">{c.pools_count ?? 0}</div>
            <div className="col-span-1 text-right flex justify-end gap-1">
              <Link to={`/admin/customers/${c.id}`} className="inline-flex items-center justify-center h-8 w-8 rounded-sm text-primary hover:bg-primary/10" title="View invoices & totals" data-testid={`view-customer-${c.id}`}>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
              <Button
                size="sm" variant="ghost"
                onClick={() => setCardCustomer(c)}
                className={`rounded-sm ${(c.card_on_file && c.card_on_file.token) ? "text-primary" : ""}`}
                title={(c.card_on_file && c.card_on_file.token)
                  ? `Card on file · ${(c.card_on_file.brand || "CARD").toUpperCase()} •••• ${c.card_on_file.last4 || "????"} — click to replace`
                  : "Add credit card on behalf of customer"}
                data-testid={`add-card-customer-${c.id}`}
              >
                <CreditCard className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="rounded-sm" data-testid={`edit-customer-${c.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(c)} className="rounded-sm text-destructive hover:text-destructive" data-testid={`delete-customer-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        </div>
      </div>

      {/* Quick "Add card on file" flow — staff enters the customer's card
          via Cardknox iFields; nothing is charged, the token is saved on
          the customer doc for future invoice / auto-charge use. */}
      <CardknoxPayDialog
        open={!!cardCustomer}
        onClose={() => setCardCustomer(null)}
        invoice={{ number: "", total: 0 }}
        customerId={cardCustomer?.id}
        mode="save-only"
        onPaid={() => {
          toast.success(`Card saved on file for ${cardCustomer?.business_name || cardCustomer?.name || "customer"}.`);
          setCardCustomer(null);
          load();
        }}
      />
    </div>
  );
}
